import { useState, useEffect, useMemo } from "react";
import { Play, Pause, Clock, Clock1 } from "lucide-react";

const TaskTimeline = ({
    task,
    user,
    handleTimelineUpdate,
    showControls = true,
    runningTaskId,
    showDropDown = false,
    employee
}) => {

    const [selectedEmployee, setSelectedEmployee] = useState("all");
    const [elapsedTime, setElapsedTime] = useState(0);
    const [showSpinner, setShowSpinner] = useState(false);
    const [localRunning, setLocalRunning] = useState(false);
    const [localStopped, setLocalStopped] = useState(false);

    const employeeList = useMemo(() => {
        const ids = task?.timeline?.map((t) => t.employeeId) || [];
        return ["all", ...new Set(ids)];
    }, [task]);

    const getUserTimeline = () => {
        if (selectedEmployee === "all") {
            return task?.timeline || [];
        }

        return (
            task?.timeline?.filter(
                (t) => t.employeeId === selectedEmployee
            ) || []
        );
    };

    const parseDate = (val) => {
        if (!val) return null;

        if (typeof val === "number") {
            return new Date(val);
        }

        if (val instanceof Date) {
            return val;
        }
        if (typeof val === "string") {
            const [date, time] = val.split(" ");

            if (!date || !time) return new Date(val);

            const [year, month, day] = date.split("-");
            const [hour, minute, second] = time.split(":");

            return new Date(
                Number(year),
                Number(month) - 1,
                Number(day),
                Number(hour),
                Number(minute),
                Number(second)
            );
        }

        return null;
    };

    const pastElapsed = useMemo(() => {
        return getUserTimeline()
            .filter((t) => t.startTime && t.endTime)
            .reduce((total, t) => {
                const start = parseDate(t.startTime);
                const end = parseDate(t.endTime);

                if (!start || !end || end < start) return total;

                return total + (end - start);
            }, 0);
    }, [task, selectedEmployee]);

    const runningEntry = useMemo(() => {
        if (localStopped) return null;

        return getUserTimeline().find(
            (t) => t.startTime && !t.endTime
        ) || null;

    }, [task?.timeline, selectedEmployee, localStopped]);

    const getInitialElapsed = () => {
        if (!runningEntry) return pastElapsed;

        const start = parseDate(runningEntry.startTime);

        if (!start || isNaN(start.getTime())) return pastElapsed;

        return Math.max(0, Date.now() - start.getTime());
    };

    useEffect(() => {
        setElapsedTime(getInitialElapsed());

        if (!runningEntry) return;
        const interval = setInterval(() => {
            const start = parseDate(runningEntry.startTime);
            if (!start) return;

            const diff = Math.max(0, Date.now() - start.getTime());

            setElapsedTime(diff);
        }, 1000);

        return () => clearInterval(interval);

    }, [runningEntry, pastElapsed]);

    const hasTime = elapsedTime > 0 || !!runningEntry;
    const isRunning = !!runningEntry;

    const displayTime = () => {
        const total = isRunning
            ? elapsedTime + pastElapsed
            : pastElapsed;

        const hours = Math.floor(total / 3600000);
        const minutes = Math.floor((total % 3600000) / 60000);
        const seconds = Math.floor((total % 60000) / 1000);

        return `${hours > 0 ? hours + "h " : ""}${minutes}m ${seconds}s`;
    };

    const handleStart = async () => {
        if (runningTaskId && runningTaskId !== task._id) {
            alert("You already have a running task.");
            return;
        }

        const alreadyRunning = task.timeline?.some(
            (t) => t.employeeId === user.userId && !t.endTime
        );

        if (alreadyRunning) {
            alert("Task already running");
            return;
        }

        setShowSpinner(true);
        setLocalStopped(false);

        try {
            await handleTimelineUpdate(task, "start");
            setLocalRunning(true);
        } finally {
            setShowSpinner(false);
        }
    };

    const handleStop = async () => {
        if (!isMyRunning) {
            alert("You did not start this task");
            return;
        }

        setShowSpinner(true);

        try {
            await handleTimelineUpdate(task, "stop");
            setLocalRunning(false);
            setLocalStopped(true);
        } finally {
            setShowSpinner(false);
        }
    };

    const isAssigned =
        task?.assignedEmployees?.some(
            (emp) => emp?.userId === user?.userId
        ) ||
        ["Admin", "Management"].includes(user?.department) ||
        task?.createdBy?.userId === user?.userId;

    const employeeMap = useMemo(() => {
        const map = {};

        employee?.forEach((emp) => {
            map[String(emp.employeeId)] =
                `${emp.firstName || ""} ${emp.lastName || ""}`.trim();
        });

        return map;
    }, [employee]);

    useEffect(() => {
        if (task?.timeline) {
            setLocalStopped(false);
        }
    }, [task?.timeline]);

    if (!isAssigned) return <div>Not assigned</div>;

    if (task.status === "complete") {
        return (
            <div className="inline-flex gap-2 items-center">
                <span className="flex items-center gap-1 text-sm text-gray-400">
                    <Clock1 size={16} /> --
                </span>
            </div>
        );
    }

    const isMyRunning = runningEntry?.employeeId === user?.userId;
    const runningUserName =
        employeeMap[String(runningEntry?.employeeId)] || "Unknown User";


    return (
        <div className="flex flex-wrap gap-2 items-center">

            {/* Time Display */}
            {hasTime ? (
                <span className="text-xs sm:text-sm text-gray-600 inline-flex items-center gap-1">
                    <Clock
                        size={16}
                        className={isRunning ? "text-green-500" : "text-gray-500"}
                    />
                    {displayTime()}
                </span>
            ) : (
                <span className="flex items-center gap-1 text-xs sm:text-sm text-gray-400">
                    <Clock1 size={16} /> --
                </span>
            )}


            {/* Start Button */}
            {showControls && !isRunning && (
                <div
                    onClick={handleStart}
                    className="flex items-center gap-1 cursor-pointer border px-3 py-1 rounded hover:bg-gray-50"
                >
                    {showSpinner && (
                        <span className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                    )}

                    <Play size={15} className="text-gray-700" />
                    <span className="text-green-600 text-xs">Start</span>
                </div>
            )}

            {isRunning && !isMyRunning && (
                <span className="text-xs text-gray-400">
                    Running by {runningUserName}
                </span>
            )}

            {/* Stop Button */}
            {showControls && isRunning && isMyRunning && (
                <div
                    onClick={handleStop}
                    className="flex items-center gap-1 cursor-pointer border px-3 py-1 rounded hover:bg-gray-50"
                >
                    {showSpinner ? (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                            <span className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></span>
                            Loading...
                        </span>
                    ) : (
                        <>
                            <Pause size={15} className="text-green-600" />
                            <span className="text-xs">Stop</span>
                        </>
                    )}
                </div>
            )}

            {showDropDown && (
                <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    className="border px-2 py-1 text-xs rounded text-gray-600"
                >
                    {employeeList.map((id) => (
                        <option key={id} value={id}>
                            {id === "all"
                                ? "All Employees"
                                : employeeMap[id] || id}
                        </option>
                    ))}
                </select>
            )}
        </div>
    );
};

export default TaskTimeline;